/**
 * Google Drive Folder Helper
 * 
 * Utility to automatically fetch the latest APK from a Google Drive folder
 * Requires folder to be publicly accessible
 */

export interface DriveFile {
    id: string;
    name: string;
    modifiedTime: string;
    size: string;
    mimeType: string;
}

/**
 * Fetches the latest APK file from a Google Drive folder
 * 
 * @param folderId Google Drive folder ID
 * @param apiKey Optional Google Drive API key (for better rate limits)
 * @returns Latest APK file info or null
 */
export async function getLatestAPKFromFolder(
    folderId: string,
    apiKey?: string
): Promise<DriveFile | null> {
    try {
        // Build API URL
        const baseUrl = 'https://www.googleapis.com/drive/v3/files';
        const params = new URLSearchParams({
            q: `'${folderId}' in parents and name contains '.apk' and trashed=false`,
            orderBy: 'modifiedTime desc',
            fields: 'files(id,name,modifiedTime,size,mimeType)',
            supportsAllDrives: 'true',
            includeItemsFromAllDrives: 'true',
            pageSize: '1' // Only get the latest file
        });

        if (apiKey) {
            params.append('key', apiKey);
        }

        const url = `${baseUrl}?${params.toString()}`;
        
        console.log('[GoogleDriveFolder] 🔍 Fetching latest APK from folder:', folderId);
        console.log('[GoogleDriveFolder] 📡 API URL:', url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[GoogleDriveFolder] API Error:', response.status, errorText);
            
            if (response.status === 403) {
                throw new Error('Folder access denied. Make sure: 1) Google Drive API is enabled, 2) Folder is shared as "Anyone with link can view", 3) Files in folder are also publicly shared.');
            }
            
            if (response.status === 404) {
                throw new Error('Folder not found. Please check the folder ID.');
            }
            
            throw new Error(`Failed to fetch files: ${response.status}`);
        }

        const data = await response.json();
        
        console.log('[GoogleDriveFolder] 📦 API Response:', data);
        
        if (!data.files || data.files.length === 0) {
            console.error('[GoogleDriveFolder] ❌ No APK files found in folder!');
            console.error('[GoogleDriveFolder] 💡 Make sure: 1) Folder contains .apk files, 2) Files are shared publicly');
            throw new Error('No APK files found in folder. Make sure the folder contains .apk files and they are shared publicly.');
        }

        const latestFile = data.files[0];
        
        console.log('[GoogleDriveFolder] ✅ Latest APK found:', latestFile.name);
        console.log('[GoogleDriveFolder] 📊 File details:', {
            name: latestFile.name,
            size: formatFileSize(parseInt(latestFile.size || '0')),
            modified: latestFile.modifiedTime
        });
        
        return {
            id: latestFile.id,
            name: latestFile.name,
            modifiedTime: latestFile.modifiedTime,
            size: formatFileSize(parseInt(latestFile.size || '0')),
            mimeType: latestFile.mimeType
        };
        
    } catch (error: any) {
        console.error('[GoogleDriveFolder] ❌ Error fetching latest APK:', error);
        console.error('[GoogleDriveFolder] 📋 Error details:', error.message || error);
        throw error; // Throw instead of returning null so we can show the error to the user
    }
}

/**
 * Downloads the latest APK from a Google Drive folder
 * 
 * @param folderId Google Drive folder ID
 * @param apiKey Optional Google Drive API key
 * @returns Download info or null
 */
export async function downloadLatestAPK(
    folderId: string,
    apiKey?: string
): Promise<{ fileId: string; filename: string; size: string } | null> {
    const latestFile = await getLatestAPKFromFolder(folderId, apiKey);
    
    if (!latestFile) {
        return null;
    }

    // Try multiple download URL formats for better compatibility
    const downloadUrl = `https://drive.google.com/uc?export=download&id=${latestFile.id}&confirm=t`;
    
    // Trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = latestFile.name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Small delay to ensure download starts
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
        fileId: latestFile.id,
        filename: latestFile.name,
        size: latestFile.size
    };
}

/**
 * Fallback: Open folder in browser for manual download
 * Use this if API method fails
 */
export function openFolderForManualDownload(folderId: string): void {
    const folderUrl = `https://drive.google.com/drive/folders/${folderId}`;
    window.open(folderUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Extracts folder ID from Google Drive folder URL
 * 
 * @param url Google Drive folder URL
 * @returns Folder ID or null
 */
export function extractGoogleDriveFolderId(url: string): string | null {
    if (!url) return null;

    try {
        // Pattern: /folders/FOLDER_ID
        const pattern1 = /\/folders\/([a-zA-Z0-9_-]+)/;
        const match1 = url.match(pattern1);
        if (match1 && match1[1]) {
            return match1[1];
        }

        // Pattern: ?id=FOLDER_ID
        const pattern2 = /[?&]id=([a-zA-Z0-9_-]+)/;
        const match2 = url.match(pattern2);
        if (match2 && match2[1]) {
            return match2[1];
        }

        // If it's already just an ID
        if (/^[a-zA-Z0-9_-]+$/.test(url)) {
            return url;
        }

        return null;
    } catch (error) {
        console.error('[GoogleDriveFolder] Error extracting folder ID:', error);
        return null;
    }
}

/**
 * Formats file size in bytes to human-readable format
 */
function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Validates if folder is accessible
 * 
 * @param folderId Google Drive folder ID
 * @param apiKey Optional API key
 * @returns true if accessible
 */
export async function validateFolderAccess(
    folderId: string,
    apiKey?: string
): Promise<boolean> {
    try {
        const file = await getLatestAPKFromFolder(folderId, apiKey);
        return file !== null;
    } catch {
        return false;
    }
}
